import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import { gsap } from "gsap";
import ApiURL from "../../controllers/Api";
import logo from "../../images/logo.png";

/** ---------- Toast (no external lib) ---------- */
function ToastStack({ toasts, onRemove }) {
  const stackRef = useRef(null);

  useEffect(() => {
    if (!stackRef.current) return;
    gsap.fromTo(
      stackRef.current.querySelectorAll(".toast-item"),
      { opacity: 0, y: 10 },
      { opacity: 1, y: 0, duration: 0.2, stagger: 0.03, ease: "power2.out" }
    );
  }, [toasts]);

  return (
    <div className="pointer-events-none fixed top-4 right-4 z-[9999] w-[min(92vw,380px)]">
      <div ref={stackRef} className="flex flex-col gap-2">
        {toasts.map((t) => {
          const styles =
            t.type === "success"
              ? "border-green-200 bg-green-50 text-green-900"
              : t.type === "error"
              ? "border-red-200 bg-red-50 text-red-900"
              : "border-[#d4af37]/25 bg-[#fff8e8] text-[#6a4b1f]";

          return (
            <div
              key={t.id}
              className={[
                "toast-item pointer-events-auto rounded-xl border px-4 py-3 shadow-sm",
                styles,
              ].join(" ")}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="text-sm">
                  {t.title && <div className="font-semibold">{t.title}</div>}
                  <div className="mt-0.5">{t.message}</div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(t.id)}
                  className="rounded-lg border border-black/10 bg-white/70 px-2 py-1 text-xs font-semibold transition hover:bg-white"
                  aria-label="Dismiss"
                >
                  ✕
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

async function safeJson(response) {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    return { success: false, message: "Invalid JSON", raw: text };
  }
}

/** Parse the backend products string:
 *  productId|productName|description|imgPath|quantity|price|size|totalAmount|igst|sgst|cgst|grandTotal|roundedAmount|roundingDifference
 *  separated by ";" for multiple items
 */
function parseProductsString(productsStr) {
  if (!productsStr) return [];
  return productsStr
    .split(";")
    .map((prod) => prod.trim())
    .filter(Boolean)
    .map((prod) => {
      const parts = prod.split("|");
      const [
        productId,
        productName,
        description,
        imgPath,
        quantity,
        price,
        size,
        totalAmount,
        igst,
        sgst,
        cgst,
        grandTotal,
        roundedAmount,
        roundingDifference,
      ] = parts;

      return {
        productId,
        productName,
        description,
        imgPath,
        quantity: Number(quantity || 0),
        price: Number(price || 0),
        size: size || "",
        totalAmount: Number(totalAmount || 0),
        igst: Number(igst || 0),
        sgst: Number(sgst || 0),
        cgst: Number(cgst || 0),
        grandTotal: Number(grandTotal || 0),
        roundedAmount: Number(roundedAmount || 0),
        roundingDifference: Number(roundingDifference || 0),
      };
    });
}

function calcOrderTotals(items) {
  const totalQty = items.reduce((s, i) => s + (Number(i.quantity) || 0), 0);
  const totalPrice = items.reduce(
    (s, i) => s + (Number(i.price) || 0) * (Number(i.quantity) || 0),
    0
  );
  const totalIGST = items.reduce((s, i) => s + (Number(i.igst) || 0), 0);
  const totalSGST = items.reduce((s, i) => s + (Number(i.sgst) || 0), 0);
  const totalCGST = items.reduce((s, i) => s + (Number(i.cgst) || 0), 0);
  const grand = items.reduce((s, i) => s + (Number(i.grandTotal) || 0), 0);

  const rounded = Math.round(grand);
  const roundingDiff = (grand - rounded) * -1;

  return {
    totalQty,
    totalPrice,
    totalIGST,
    totalSGST,
    totalCGST,
    grand,
    rounded,
    roundingDiff,
  };
}

const OrderSummary = () => {
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterOpen, setFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [customerNameFilter, setCustomerNameFilter] = useState("");

  // Toasts
  const [toasts, setToasts] = useState([]);

  const wrapRef = useRef(null);
  const filterRef = useRef(null);
  const tableContainerRef = useRef(null);

  const toast = (type, message, title) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setToasts((p) => [...p, { id, type, message, title }]);
    window.setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 2800);
  };
  const removeToast = (id) => setToasts((p) => p.filter((t) => t.id !== id));

  // Horizontal scroll functions (mobile + desktop)
  const scrollTableLeft = () => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollBy({ left: -320, behavior: "smooth" });
    }
  };

  const scrollTableRight = () => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollBy({ left: 320, behavior: "smooth" });
    }
  };

  // close filter on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (!filterRef.current) return;
      if (filterRef.current.contains(e.target)) return;
      setFilterOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${ApiURL}/getReportData.php`);
      const data = await safeJson(res);
console.log(data)
      if (data?.success) {
        const incoming = data?.data?.recent_orders || data?.data?.orders || [];
        setAllOrders(Array.isArray(incoming) ? incoming : []);
      } else {
        toast("error", data?.message || "Failed to fetch orders.");
        setAllOrders([]);
      }
    } catch (err) {
      console.error(err);
      toast("error", "An error occurred while fetching order details.");
      setAllOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // GSAP entrance
  useLayoutEffect(() => {
    if (!wrapRef.current) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".os-anim",
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0, duration: 0.45, stagger: 0.06, ease: "power2.out" }
      );
    }, wrapRef);
    return () => ctx.revert();
  }, []);

  const normalizedOrders = useMemo(() => {
    return (allOrders || []).map((o) => {
      const items = parseProductsString(o.products);
      const totals = calcOrderTotals(items);
      return {
        ...o,
        items,
        totals,
        orderDateObj: o.order_date ? new Date(o.order_date) : null,
      };
    });
  }, [allOrders]);

  const filteredOrders = useMemo(() => {
    let list = [...normalizedOrders];

    if (statusFilter) {
      list = list.filter(
        (o) => String(o.status || "").toLowerCase() === statusFilter.toLowerCase()
      );
    }

    if (dateFrom) {
      const from = new Date(dateFrom);
      list = list.filter((o) => o.orderDateObj && o.orderDateObj >= from);
    }

    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      list = list.filter((o) => o.orderDateObj && o.orderDateObj <= to);
    }

    if (customerNameFilter.trim()) {
      const q = customerNameFilter.trim().toLowerCase();
      list = list.filter((o) => String(o.full_name || "").toLowerCase().includes(q));
    }

    return list;
  }, [normalizedOrders, statusFilter, dateFrom, dateTo, customerNameFilter]);

  const resetFilters = () => {
    setStatusFilter("");
    setDateFrom("");
    setDateTo("");
    setCustomerNameFilter("");
    toast("info", "Filters reset.");
  };

  /** CSV helpers */
  const downloadCsv = (filename, rows) => {
    const csv = rows
      .map((r) =>
        r
          .map((cell) => {
            const v = cell == null ? "" : String(cell);
            const escaped = v.replace(/"/g, '""');
            return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
          })
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportOrderSummaryCsv = () => {
    const headers = [
      "SNo",
      "Order ID",
      "Customer Name",
      "Order Date",
      "Status",
      "Total Quantity",
      "Total Price",
      "Total IGST",
      "Total SGST",
      "Total CGST",
      "Grand Amount",
      "Rounding Difference",
      "Payable Amount",
    ];

    const rows = filteredOrders.map((o, idx) => [
      idx + 1,
      o.order_id,
      o.full_name,
      o.order_date ? new Date(o.order_date).toLocaleDateString() : "",
      o.status,
      o.totals.totalQty,
      o.totals.totalPrice.toFixed(2),
      o.totals.totalIGST.toFixed(2),
      o.totals.totalSGST.toFixed(2),
      o.totals.totalCGST.toFixed(2),
      o.totals.grand.toFixed(2),
      o.totals.roundingDiff.toFixed(2),
      o.totals.rounded.toFixed(2),
    ]);

    downloadCsv("order_summary.csv", [headers, ...rows]);
    toast("success", "Order summary CSV downloaded.");
  };

  const exportOrderItemsCsv = () => {
    const headers = [
      "Order ID",
      "Customer Name",
      "Order Date",
      "Status",
      "Product Name",
      "Size",
      "Qty",
      "Unit Price",
      "IGST",
      "SGST",
      "CGST",
      "Grand Total",
    ];

    const rows = [];
    filteredOrders.forEach((o) => {
      (o.items || []).forEach((it) => {
        rows.push([
          o.order_id,
          o.full_name,
          o.order_date ? new Date(o.order_date).toLocaleDateString() : "",
          o.status,
          it.productName,
          it.size,
          it.quantity,
          Number(it.price || 0).toFixed(2),
          Number(it.igst || 0).toFixed(2),
          Number(it.sgst || 0).toFixed(2),
          Number(it.cgst || 0).toFixed(2),
          Number(it.grandTotal || 0).toFixed(2),
        ]);
      });
    });

    downloadCsv("order_items.csv", [headers, ...rows]);
    toast("success", "Order items CSV downloaded.");
  };

  if (loading) {
    return <div className="min-h-screen bg-[#F7EFE7] p-6 font-[Kanit] text-[#6f5e57]">Loading orders...</div>;
  }

  if (!filteredOrders || filteredOrders.length === 0) {
    return (
      <div className="min-h-screen bg-[#F7EFE7] p-4 font-[Kanit] sm:p-6">
        <ToastStack toasts={toasts} onRemove={removeToast} />
        <div className="rounded-[24px] border border-[#d8c6b7] bg-[#fffaf5] p-6 shadow-[0_20px_55px_rgba(43,0,0,0.08)]">
          <div className="font-serif text-2xl font-medium text-[#2B0000]">Order Summary</div>
          <div className="mt-2 text-sm text-[#6f5e57]">
            No orders available for the current filters.
          </div>

          <button
            type="button"
            onClick={() => {
              resetFilters();
              fetchOrders();
            }}
            className="mt-4 rounded-xl bg-[#8f6f38] px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-[#765a2d]"
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="order-summary-luxury min-h-screen w-full overflow-x-hidden bg-[#F7EFE7] p-3 font-[Kanit] text-[#2B0000] sm:p-5 lg:p-7">
      <ToastStack toasts={toasts} onRemove={removeToast} />

      <div className="os-anim mx-auto max-w-[1650px] rounded-[26px] border border-[#d8c6b7] bg-[#fffaf5] p-4 shadow-[0_22px_60px_rgba(43,0,0,0.08)] sm:p-6 lg:p-8">
        {/* Header */}
        <div className="os-anim flex flex-col gap-5 border-b border-[#e3d6cb] pb-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="logo" className="h-12 w-12 rounded-xl border border-[#d4af37]/30 bg-white object-contain p-1 shadow-sm" />
            <div>
              <div className="font-serif text-2xl font-medium text-[#2B0000] sm:text-3xl">Order Summary</div>
              <div className="text-sm text-[#6f5e57]">SL GOLD Reporting</div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setFilterOpen((v) => !v)}
              className="rounded-xl bg-[#2B0000] px-4 py-2.5 text-sm font-semibold text-[#f4dfae] shadow-md transition hover:-translate-y-0.5 hover:bg-[#4A090B]"
            >
              Filters
            </button>

            <button
              type="button"
              onClick={exportOrderSummaryCsv}
              className="rounded-xl bg-[#8f6f38] px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-[#765a2d]"
            >
              Export Summary CSV
            </button>

            <button
              type="button"
              onClick={exportOrderItemsCsv}
              className="rounded-xl border border-[#d6c4b5] bg-white px-4 py-2.5 text-sm font-semibold text-[#2B0000] transition hover:bg-[#fff7ed]"
            >
              Export Items CSV
            </button>

            <button
              type="button"
              onClick={fetchOrders}
              className="rounded-xl border border-[#d6c4b5] bg-white px-4 py-2.5 text-sm font-semibold text-[#2B0000] transition hover:bg-[#fff7ed]"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {filterOpen && (
          <div ref={filterRef} className="os-anim mt-5 rounded-[22px] border border-[#d8c6b7] bg-[linear-gradient(145deg,#fffaf5,#f3e7dc)] p-4 shadow-[0_16px_40px_rgba(43,0,0,0.06)] sm:p-5">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="block text-sm font-semibold text-[#4a352f]">Order Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                >
                  <option value="">All</option>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#4a352f]">Date From</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#4a352f]">Date To</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#4a352f]">Customer Name</label>
                <input
                  type="text"
                  value={customerNameFilter}
                  onChange={(e) => setCustomerNameFilter(e.target.value)}
                  placeholder="Search by name"
                  className="mt-2 w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                />
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={resetFilters}
                className="rounded-xl border border-[#d6c4b5] bg-[#f1e6dc] px-4 py-2.5 text-sm font-semibold text-[#2B0000] transition hover:bg-[#e8d8ca]"
              >
                Reset
              </button>

              <button
                type="button"
                onClick={() => {
                  setFilterOpen(false);
                  toast("success", "Filters applied.");
                }}
                className="rounded-xl bg-[#2B0000] px-4 py-2.5 text-sm font-semibold text-[#f4dfae] shadow-md transition hover:-translate-y-0.5 hover:bg-[#4A090B]"
              >
                Apply
              </button>
            </div>
          </div>
        )}

        {/* Horizontal scroll controls (now visible on mobile too) */}
        <div className="os-anim mt-6 flex items-center justify-between gap-3 rounded-[18px] border border-[#e2d4c8] bg-[#fbf3eb] p-3">
          <button
            type="button"
            onClick={scrollTableLeft}
            className="flex items-center justify-center rounded-xl border border-[#d6c4b5] bg-white px-3 py-2 text-sm font-semibold text-[#2B0000] transition-all duration-200 hover:bg-[#fff7ed] active:translate-y-[1px]"
            aria-label="Scroll table left"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="ml-2 hidden sm:inline">Scroll Left</span>
          </button>

          <div className="text-center text-xs font-medium text-[#75645d] sm:text-sm">
            Swipe or use buttons to scroll horizontally
          </div>

          <button
            type="button"
            onClick={scrollTableRight}
            className="flex items-center justify-center rounded-xl border border-[#d6c4b5] bg-white px-3 py-2 text-sm font-semibold text-[#2B0000] transition-all duration-200 hover:bg-[#fff7ed] active:translate-y-[1px]"
            aria-label="Scroll table right"
          >
            <span className="mr-2 hidden sm:inline">Scroll Right</span>
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* TABLE (VISIBLE ON MOBILE + DESKTOP) */}
        <div className="os-anim mt-3">
          <div
            ref={tableContainerRef}
            className="overflow-x-auto rounded-[22px] border border-[#d8c6b7] bg-white shadow-[0_16px_42px_rgba(43,0,0,0.06)] scroll-smooth overscroll-x-contain"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "#8f6f38 #f4e8dd",
              WebkitOverflowScrolling: "touch",
              touchAction: "pan-x",
            }}
          >
            <table className="min-w-[1200px] w-full text-left text-sm">
              <thead className="sticky top-0 bg-[#eee2d7] text-xs font-semibold uppercase tracking-[0.12em] text-[#4b352f]">
                <tr>
                  <th className="px-4 py-3">SNo</th>
                  <th className="px-4 py-3">Order ID</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Order Date</th>
                  <th className="px-4 py-3">Status</th>

                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Size</th>
                  <th className="px-4 py-3 text-right">Qty</th>
                  <th className="px-4 py-3 text-right">Unit</th>

                  <th className="px-4 py-3 text-right">IGST</th>
                  <th className="px-4 py-3 text-right">SGST</th>
                  <th className="px-4 py-3 text-right">CGST</th>

                  <th className="px-4 py-3 text-right font-bold">Grand</th>
                  <th className="px-4 py-3 text-right font-bold">Round Diff</th>
                  <th className="px-4 py-3 text-right font-bold">Payable</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-[#eadfd5]">
                {filteredOrders.map((o, idx) => {
                  const items = o.items || [];
                  const totals = o.totals;

                  return items.map((it, itIdx) => (
                    <tr key={`${o.order_id}-${itIdx}`} className="transition hover:bg-[#fff5ea]">
                      {itIdx === 0 && (
                        <>
                          <td rowSpan={items.length} className="px-4 py-3 align-top font-semibold text-[#2B0000]">
                            {idx + 1}
                          </td>
                          <td rowSpan={items.length} className="px-4 py-3 align-top font-semibold text-[#2B0000]">
                            {o.order_id}
                          </td>
                          <td rowSpan={items.length} className="px-4 py-3 align-top">
                            <div className="font-semibold text-[#2B0000]">{o.full_name}</div>
                          </td>
                          <td rowSpan={items.length} className="px-4 py-3 align-top text-[#5d4b44]">
                            {o.order_date ? new Date(o.order_date).toLocaleDateString() : ""}
                          </td>
                          <td rowSpan={items.length} className="px-4 py-3 align-top">
                            <span
                              className={[
                                "inline-flex rounded-full px-2.5 py-1 text-xs font-semibold",
                                String(o.status).toLowerCase() === "completed"
                                  ? "bg-green-100 text-green-800"
                                  : String(o.status).toLowerCase() === "pending"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-red-100 text-red-800",
                              ].join(" ")}
                            >
                              {o.status}
                            </span>
                          </td>
                        </>
                      )}

                      {/* Item columns */}
                      <td className="px-4 py-3 text-[#2B0000]">{it.productName}</td>
                      <td className="px-4 py-3 text-[#5d4b44]">{it.size}</td>
                      <td className="px-4 py-3 text-right text-[#2B0000]">{it.quantity}</td>
                      <td className="px-4 py-3 text-right text-[#2B0000]">
                        ₹{Number(it.price || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right text-[#2B0000]">
                        ₹{Number(it.igst || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right text-[#2B0000]">
                        ₹{Number(it.sgst || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right text-[#2B0000]">
                        ₹{Number(it.cgst || 0).toFixed(2)}
                      </td>

                      {/* Totals: show once per order (rowSpan) */}
                      {itIdx === 0 && (
                        <>
                          <td rowSpan={items.length} className="px-4 py-3 align-top text-right font-bold text-[#2B0000]">
                            ₹{totals.grand.toFixed(2)}
                          </td>
                          <td rowSpan={items.length} className="px-4 py-3 align-top text-right font-bold text-[#2B0000]">
                            ₹{totals.roundingDiff.toFixed(2)}
                          </td>
                          <td rowSpan={items.length} className="px-4 py-3 align-top text-right font-bold text-[#2B0000]">
                            ₹{totals.rounded.toFixed(2)}
                          </td>
                        </>
                      )}
                    </tr>
                  ));
                })}
              </tbody>
            </table>
          </div>

          {/* Small-screen helper */}
          <div className="mt-2 text-xs text-[#7d6c64] sm:hidden">
            Tip: swipe left/right on the table to view all columns.
          </div>
        </div>
      </div>

      <style>{`
        .order-summary-luxury {
          background:
            radial-gradient(circle at 6% 0%, rgba(212,175,55,.10), transparent 28%),
            radial-gradient(circle at 95% 92%, rgba(43,0,0,.04), transparent 30%),
            #F7EFE7;
        }

        .order-summary-luxury input,
        .order-summary-luxury select,
        .order-summary-luxury button {
          font: inherit;
        }

        .order-summary-luxury table {
          border-collapse: separate;
          border-spacing: 0;
        }

        .order-summary-luxury th {
          border-bottom: 1px solid #d8c6b7;
        }

        .order-summary-luxury ::selection {
          color: #fff7e7;
          background: #7a3131;
        }

        .order-summary-luxury * {
          scrollbar-width: thin;
          scrollbar-color: rgba(143,111,56,.55) rgba(212,175,55,.07);
        }

        .order-summary-luxury *::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .order-summary-luxury *::-webkit-scrollbar-track {
          background: rgba(212,175,55,.07);
        }

        .order-summary-luxury *::-webkit-scrollbar-thumb {
          border: 2px solid transparent;
          border-radius: 999px;
          background: rgba(143,111,56,.55);
          background-clip: padding-box;
        }

        @media (max-width: 640px) {
          .order-summary-luxury {
            padding: 10px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .order-summary-luxury *,
          .order-summary-luxury *::before,
          .order-summary-luxury *::after {
            transition-duration: .01ms !important;
            animation-duration: .01ms !important;
            animation-iteration-count: 1 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default OrderSummary;